import * as React from "react";

export const THEME_STORAGE_KEY = "admin:theme";

const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";

/** What the admin picked. `system` defers to the OS setting. */
export type ThemePreference = "light" | "dark" | "system";

/** What is actually painted once `system` has been resolved. */
export type ResolvedTheme = "light" | "dark";

const PREFERENCES: readonly ThemePreference[] = ["light", "dark", "system"];

function isThemePreference(value: unknown): value is ThemePreference {
  return PREFERENCES.includes(value as ThemePreference);
}

/**
 * Collapses a preference and the current OS setting into the theme to paint.
 *
 * @param preference - the admin's stored choice
 * @param systemPrefersDark - whether the OS currently asks for a dark UI
 */
export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light";
  return preference;
}

function readStoredPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.(DARK_MEDIA_QUERY).matches ?? false;
}

function applyTheme(resolved: ResolvedTheme) {
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

/**
 * Reads, applies and persists the admin console's colour theme.
 *
 * The resolved theme is written to the `dark` class on `<html>`, matching the
 * `@custom-variant dark` rule in `theme.css`. An inline script in `index.html`
 * applies the same class before first paint and must resolve identically --
 * any value other than `light` or `dark` means `system` -- or the page flashes
 * one theme before settling on the other.
 *
 * While the preference is `system` the hook tracks live OS changes.
 */
export function useTheme() {
  const [preference, setPreferenceState] =
    React.useState<ThemePreference>(readStoredPreference);
  const [osPrefersDark, setOsPrefersDark] =
    React.useState<boolean>(systemPrefersDark);

  React.useEffect(() => {
    const media = window.matchMedia?.(DARK_MEDIA_QUERY);
    if (!media) return;

    const onChange = (event: MediaQueryListEvent) =>
      setOsPrefersDark(event.matches);

    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  const resolved = resolveTheme(preference, osPrefersDark);

  React.useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = React.useCallback((value: ThemePreference) => {
    setPreferenceState(value);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      // Persisting is best-effort; a failed write must never break the toggle.
    }
  }, []);

  return { preference, resolved, setPreference };
}
